

import { useState, useEffect } from "react"
import axios from "axios"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import "bootstrap/dist/css/bootstrap.min.css"
import { toWords } from "number-to-words"
import jsPDF from "jspdf"
import "jspdf-autotable"
import "../App.css"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUser } from "@fortawesome/free-solid-svg-icons"
import { Link } from "react-router-dom"
import { format } from "date-fns"

const Receipt = () => {
  // Previous state declarations
  const [searchInput, setSearchInput] = useState("")
  const [userExists, setUserExists] = useState(false)
  const [formData, setFormData] = useState({
    receiptNo: "",
    mobileNo: "",
    name: "",
    date: new Date().toISOString().split("T")[0],
    amount: "",
    amountType: "",
    description: "",
    amountInWords: "",
  })
  const [receipt, setReceipt] = useState([])
  const [receipts, setReceipts] = useState([])
  const [dateFilter, setDateFilter] = useState({
    startDate: "",
    endDate: "",
    mobileNumber: "",
  })
  // New state for filter type
  const [filterType, setFilterType] = useState("all")
  const [globalFilter, setGlobalFilter] = useState(false)
  // Add new state variables for page total and grand total
  const [pageTotal, setPageTotal] = useState(0)
  const [grandTotal, setGrandTotal] = useState(0)
  // Add new state variables for custom date range
  const [customDateRange, setCustomDateRange] = useState({
    fromDate: "",
    toDate: "",
  })
  // Add this new state variable at the beginning of the component, with the other state declarations
  const [containersVisible, setContainersVisible] = useState(false)

  // Previous useEffect and handlers remain the same
  useEffect(() => {
    const fetchLastReceiptNo = async () => {
      try {
        const response = await axios.get("https://node.ricehr.com/receiptdetails/last")
        if (response.data && response.data.lastReceiptNo !== undefined) {
          setFormData((prevData) => ({
            ...prevData,
            receiptNo: response.data.lastReceiptNo + 1,
          }))
        } else {
          setFormData((prevData) => ({
            ...prevData,
            receiptNo: 1,
          }))
        }
      } catch (error) {
        console.error("Error fetching last receipt number:", error)
        toast.error("Failed to fetch the last receipt number. Please check your backend.")
      }
    }

    fetchLastReceiptNo()
  }, [])

  // Previous handlers remain the same
  const handleSearch = async (e) => {
    e.preventDefault()
    try {
      const response = await axios.post("https://node.ricehr.com/checkmobile", { mobile: searchInput })
      if (response.data.exists) {
        setUserExists(true)
        setFormData((prevData) => ({
          ...prevData,
          mobileNo: searchInput,
          name: response.data.name,
        }))
        fetchReceiptData(searchInput)
        setDateFilter((prev) => ({ ...prev, mobileNumber: searchInput }))
      } else {
        setUserExists(false)
        toast.info("User not found, please register.")
      }
    } catch (error) {
      console.error("Error checking mobile number:", error)
      toast.error("Something went wrong. Please try again.")
    }
  }
  // Utility function to convert decimal amount to words
  const convertAmountToWords = (amount) => {
    if (!amount) return ""

    const [whole, fractional] = String(amount).split(".")

    const wholeInWords = toWords(Number(whole))
    const fractionalInWords = fractional ? toWords(Number(fractional)) : ""

    return fractionalInWords ? `${wholeInWords} point ${fractionalInWords}` : wholeInWords
  }

  // Previous handlers remain the same
  const handleChange = (e) => {
    const { name, value } = e.target

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }))

    if (name === "amount") {
      const amountInWords = convertAmountToWords(value)
      setFormData((prevData) => ({
        ...prevData,
        amountInWords: amountInWords,
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const response = await axios.post("https://node.ricehr.com/receiptdetails", formData)
      toast.success(response.data.message)
      fetchReceiptData(formData.mobileNo)

      setFormData((prevData) => ({
        ...prevData,
        receiptNo: prevData.receiptNo + 1,
        date: "",
        amount: "",
        amountType: "",
        description: "",
        amountInWords: "",
      }))
    } catch (error) {
      console.error("Error:", error)
      toast.error("Failed to save details. Please check your backend.")
    }
  }

  // Add new function for global filtering
  const fetchAllReceipts = async () => {
    try {
      const response = await axios.get("https://node.ricehr.com/receiptdetailsall")
      setReceipts(response.data)
    } catch (error) {
      console.error("Error fetching all receipts:", error)
      toast.error(
        error.response?.status === 404
          ? "The all receipts endpoint is not available. Please check your server configuration."
          : "Failed to fetch all receipts. Please try again.",
      )
      // Reset the global filter if the endpoint fails
      setGlobalFilter(false)
    }
  }

  const formatDate = (date) => {
    const d = new Date(date)
    const day = String(d.getDate()).padStart(2, "0") // Ensure two-digit day
    const month = String(d.getMonth() + 1).padStart(2, "0") // Months are 0-based
    const year = d.getFullYear()
    return `${day}-${month}-${year}` // Format: DD-MM-YYYY
  }

  const handleFilterTypeChange = async (e) => {
    const type = e.target.value
    setFilterType(type)

    if (type !== "custom") {
      try {
        let data = []

        if (globalFilter) {
          const response = await axios.get("https://node.ricehr.com/receiptdetailsall")
          data = response.data
        } else {
          if (!dateFilter.mobileNumber) {
            toast.error("Please select a mobile number first")
            return
          }
          const response = await axios.get(`https://node.ricehr.com/getreceipt/${dateFilter.mobileNumber}`)
          data = response.data
        }

        let filteredData = []
        const today = formatDate(new Date())

        switch (type) {
          case "today":
            filteredData = data.filter((item) => formatDate(item.Date) === today)
            break

          case "month":
            const currentMonth = new Date().getMonth()
            const currentYear = new Date().getFullYear()
            const startOfMonth = new Date(currentYear, currentMonth, 1)
            const endOfMonth = new Date(currentYear, currentMonth + 1, 0)

            setDateFilter((prev) => ({
              ...prev,
              startDate: formatDate(startOfMonth),
              endDate: formatDate(endOfMonth),
            }))

            filteredData = data.filter((item) => {
              const itemDate = new Date(item.Date)
              return itemDate >= startOfMonth && itemDate <= endOfMonth
            })
            break

          case "all":
          default:
            filteredData = data
        }

        setReceipt(filteredData)
      } catch (error) {
        console.error("Error applying filter:", error)
        toast.error("Failed to apply filter")
      }
    }
  }

  const handleDateFilterChange = (e) => {
    const { name, value } = e.target
    setDateFilter((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleFilterSubmit = async (e) => {
    e.preventDefault()
    if (!dateFilter.mobileNumber && !globalFilter) {
      toast.error("Please select a mobile number first")
      return
    }
    try {
      let response
      if (globalFilter) {
        response = await axios.get("https://node.ricehr.com/receiptdetailsall")
      } else {
        response = await axios.get(`https://node.ricehr.com/getreceipt/${dateFilter.mobileNumber}`)
      }
      let filteredData = response.data

      if (filterType === "today") {
        const today = new Date().toISOString().split("T")[0]
        filteredData = response.data.filter((item) => {
          const itemDate = new Date(item.Date).toISOString().split("T")[0]
          return itemDate === today
        })
      } else if (filterType === "month") {
        const currentDate = new Date()
        const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
        const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)

        filteredData = response.data.filter((item) => {
          const itemDate = new Date(item.Date)
          return itemDate >= firstDayOfMonth && itemDate <= lastDayOfMonth
        })
      } else if (filterType === "custom" && customDateRange.fromDate && customDateRange.toDate) {
        const startDate = new Date(customDateRange.fromDate)
        const endDate = new Date(customDateRange.toDate)
        endDate.setHours(23, 59, 59, 999) // Set to end of day

        filteredData = response.data.filter((item) => {
          const itemDate = new Date(item.Date)
          return itemDate >= startDate && itemDate <= endDate
        })

        setDateFilter((prev) => ({
          ...prev,
          startDate: formatDate(startDate),
          endDate: formatDate(endDate),
        }))
      }

      setReceipt(filteredData)
    } catch (error) {
      console.error("Error fetching filtered receipt data:", error)
      toast.error("Failed to fetch filtered data")
    }
  }

  const resetFilters = () => {
    setDateFilter({
      startDate: "",
      endDate: "",
      mobileNumber: dateFilter.mobileNumber,
    })
    setFilterType("all")
    setCustomDateRange({ fromDate: "", toDate: "" })
    if (globalFilter) {
      fetchAllReceipts()
    } else {
      fetchReceiptData(dateFilter.mobileNumber)
    }
  }

  // Previous handlers remain the same
  const generatePdf = async () => {
    const doc = new jsPDF()

    doc.setFontSize(16)
    doc.text("Receipt Details", 105, 20, null, null, "center")

    // Add date range information on the same line
    doc.setFontSize(12)
    const fromDate = dateFilter.startDate || customDateRange.fromDate || "N/A"
    const toDate = dateFilter.endDate || customDateRange.toDate || "N/A"
    doc.text(`From Date: ${fromDate}`, 14, 30)
    doc.text(`To Date: ${toDate}`, doc.internal.pageSize.width - 14, 30, { align: "right" })

    const headers = ["SNo", "ReceiptNo", "Name", "Amount"]
    const data = receipt.map((item, index) => [index + 1, item.ReceiptNo, item.Name, item.Amount])

    doc.autoTable({
      startY: 36, // Adjusted startY to accommodate the new date range layout
      head: [headers],
      body: data,
      theme: "grid",
    })

    // Add page total and grand total
    const finalY = doc.lastAutoTable.finalY || 36
    doc.setFontSize(12)
    // doc.text(`Page Total: ${pageTotal.toFixed(2)}`, 14, finalY + 10)
    doc.text(`Grand Total: ${grandTotal.toFixed(2)}`, doc.internal.pageSize.width - 14, finalY + 20, { align: "right" })

    const dateRange =
      dateFilter.startDate && dateFilter.endDate ? `-${dateFilter.startDate}-to-${dateFilter.endDate}` : ""

    doc.save(`receipt-details${dateRange}.pdf`)

    return doc.output("blob")
  }

  const handlePrintReceipt = () => {
    const receiptData = {
      ReceiptNo: formData.receiptNo,
      Date: formData.date,
      Name: formData.name,
      Amount: formData.amount,
      AmountInWords: formData.amountInWords,
      AmountType: formData.amountType,
      Description: formData.description,
    }
    generateReceipt(receiptData)
  }

  const fetchReceiptData = async (mobileNo) => {
    try {
      const response = await axios.get(`https://node.ricehr.com/getreceipt/${mobileNo}`)
      setReceipt(response.data)
    } catch (error) {
      console.error("Error fetching receipt data:", error.response?.data || error.message)
      toast.error("Failed to fetch receipt data. Please check your backend.")
    }
  }

  useEffect(() => {
    const total = receipt.reduce((sum, item) => sum + Number.parseFloat(item.Amount), 0)
    setPageTotal(total)
    setGrandTotal(total) // For now, page total and grand total are the same
  }, [receipt])
  const generateReceipt = (receipt) => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a5",
    });
  
    try {
      // Add banner image
      doc.addImage("/assets/banner.png", "PNG", 10, 10, 190, 30);
  
      // Add header text
      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.setTextColor(220, 20, 20);
      doc.text("SATYAM SHIVAM SUNDARAM GAUNIVAS", doc.internal.pageSize.width / 2, 50, {
        align: "center",
      });
  
      // Receipt details
      const startX = 20;
      let startY = 60;
      const lineHeight = 8;
      const valueX = 80; // Value X for left-aligned text
  
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
  
// Receipt No (Left side)
doc.setFont("helvetica", "bold");
doc.text("Receipt No:", startX, startY);

// Set font to normal for value and color to red
doc.setFont("helvetica", "bold");  // Keeping bold for the value
doc.setTextColor(220, 20, 20); // Set text color to red (RGB: 220, 20, 20)
doc.text(`SSG ${receipt.ReceiptNo}`, valueX, startY);

// Reset text color to black for the next elements
doc.setTextColor(0, 0, 0); // Reset text color back to black

// Adjust Date X position to move it further to the right (by 60 units)
const formattedDate =
  receipt.Date && !isNaN(new Date(receipt.Date)) ? format(new Date(receipt.Date), "dd/MM/yyyy") : "N/A";

// Add 60 units to move Date further right, and then subtract 10 to move it to the left
const dateX = doc.internal.pageSize.width - 30 - doc.getStringUnitWidth(formattedDate) * doc.internal.getFontSize() + 60 - 10;

// Date label and value
doc.setFont("helvetica", "bold");
doc.text("Date:", dateX - 30, startY);  // Adjust label slightly for better placement
doc.setFont("helvetica", "normal");
doc.text(formattedDate, dateX, startY);

// Adjust startY for the next fields (move down if needed)
startY += lineHeight;

  
     // Move startY down by 30+ units
startY += 10; // Add 30 units to move the "Name of Donor" field lower

// Name of Donor
doc.setFont("helvetica", "bold");
doc.text("Name of Donor:", startX, startY);
doc.setFont("helvetica", "normal");
doc.text(`${receipt.Name || "N/A"}`, valueX, startY);

startY += lineHeight;  // Move down for the next line

  
      const formattedAmount = new Intl.NumberFormat("en-IN", {
        style: "decimal", // Changed from "currency" to "decimal" to avoid adding the ₹ symbol
        minimumFractionDigits: 2,
      }).format(receipt.Amount || 0);
      
      doc.setFont("helvetica", "bold");
      doc.text("Amount Received:", startX, startY);
      doc.setFont("helvetica", "normal");
      doc.text(`${formattedAmount} /-`, valueX, startY); // Display amount without ₹ and add '/-'
      
      startY += lineHeight;
      
  
      // Rupees in Words
      doc.setFont("helvetica", "bold");
      doc.text("Rupees:", startX, startY);
      doc.setFont("helvetica", "normal");
      doc.text(`${receipt.AmountInWords || "Zero"} only`, valueX, startY);
  
      startY += lineHeight;
  
      // Payment Mode
      doc.setFont("helvetica", "bold");
      doc.text("Payment Mode:", startX, startY);
      doc.setFont("helvetica", "normal");
      doc.text(`${receipt.AmountType || "N/A"}`, valueX, startY);
  
      startY += lineHeight;
  
      // Purpose
      doc.setFont("helvetica", "bold");
      doc.text("Purpose:", startX, startY);
      doc.setFont("helvetica", "normal");
      doc.text(`${receipt.Description || "N/A"}`, valueX, startY);
  
      // Add QR Code
      doc.addImage("/assets/qrcode.jpeg", "JPEG", 150, 75, 40, 40);
  
      // Add signature line
      const signatureY = doc.internal.pageSize.height - 25;
      doc.line(doc.internal.pageSize.width - 60, signatureY, doc.internal.pageSize.width - 20, signatureY);
      doc.text("Authorized Signatory", doc.internal.pageSize.width - 40, signatureY + 5, { align: "center" });
  
      // Print handling
      doc.autoPrint();
      const pdfOutput = doc.output("bloburl");
      window.open(pdfOutput, "_blank");
    } catch (error) {
      console.error("Error generating receipt:", error);
      toast.error("Failed to generate receipt");
    }
  };
    
  // Add this new function after the other handler functions
  const toggleContainers = () => {
    setContainersVisible((prev) => !prev)
  }

  return (
    <div className="container-fluid bg-light py-5">
      <div className="container">
        {/* <div className="card shadow-lg mb-4 mt-2 mb-3 text-center "  >
        <div className="card-body py-3">
          <h2 >SATYAM SHIVAM SUNDARAM GAU NIVAS HYDERABAD</h2>
          
          </div>
          <h5 className="text-center mb-5">RECEIPT MANAGEMENT</h5>
          </div> */}

        <div className="flex justify-center items-center min-h-screen bg-gradient-to-r from-blue-600 to-purple-600 mb-5">
          <div className="bg-white shadow-lg rounded-2xl p-6 w-[400px] text-center border border-gray-200">
            <div className="bg-gradient-to-r from-indigo-500 to-blue-500 text-dark rounded-t-2xl p-4">
              <h2 className="text-xl font-bold tracking-wide">SATYAM SHIVAM SUNDARAM GAU NIVAS HYDERABAD</h2>
            </div>
            <div className="p-6">
              <h5 className="text-lg font-semibold text-gray-700 uppercase tracking-wide">Receipt Management</h5>
            </div>
          </div>
        </div>
        {/* Search Form */}
        <div className="card shadow-lg mb-4">
          <div className="card-body">
            <form onSubmit={handleSearch}>
              <div className="mb-3">
                <label htmlFor="mobileNumber" className="form-label">
                  Mobile Number
                </label>
                <div className="input-group">
                  <input
                    type="tel"
                    className="form-control"
                    id="mobileNumber"
                    placeholder="Enter mobile number"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    required={!globalFilter}
                    maxLength={10}
                  />
                  <button type="submit" className="btn btn-primary" disabled={globalFilter}>
                    Search
                  </button>
                </div>
              </div>

              <Link to="/registrationpage" style={{ color: "blue", textDecoration: "none", fontWeight: "500" }}>
                <span style={{ marginRight: "10px", color: "black" }}>New Donar Please</span>
                <FontAwesomeIcon icon={faUser} /> Register
              </Link>
            </form>
          </div>
        </div>

        {/* Receipt Form - Only show when mobile number is selected and not in global filter mode */}
        {userExists && !globalFilter && (
          <>
            {/* Receipt Form */}
            <div className="card shadow-lg mb-4">
              <div className="card-body">
                <div className="text-center mb-4 ">
                  <h3 className="font-bold text-2xl">Receipt : {formData.receiptNo}</h3>
                </div>
                <form onSubmit={handleSubmit}>
                  {/* Previous form fields remain the same */}
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Mobile No.</label>
                      <input type="tel" className="form-control" name="mobileNo" value={formData.mobileNo} readOnly />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Name Of Donar</label>
                      <input type="text" className="form-control" name="Name" value={formData.name} readOnly />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Type of Payment</label>
                      <select
                        className="form-select"
                        name="amountType"
                        value={formData.amountType}
                        onChange={handleChange}
                        required
                      >
                        <option value="">--Select--</option>
                        <option value="UPI">UPI</option>
                        <option value="CASH">CASH</option>
                        <option value="CHEQUE">CHEQUE</option>
                        <option value="KIND">KIND</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Amount / Value</label>
                      <input
                        type="number"
                        step="0.01" // Allow decimal values
                        className="form-control"
                        name="amount"
                        value={formData.amount}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Amount in Words</label>
                      <input
                        type="text"
                        className="form-control"
                        name="amountInWords"
                        value={formData.amountInWords}
                        readOnly
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">Description</label>
                      <textarea
                        rows={1}
                        className="form-control"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                      ></textarea>
                    </div>
                  </div>
                  <div className="d-flex justify-content-center gap-3 mt-4">
                    <button type="submit" className="btn btn-primary btn-lg">
                      Submit
                    </button>
                    {/* <button type="button" className="btn btn-warning btn-lg" onClick={generatePdf}>
                      Print & Send PDF
                    </button> */}
                    <button type="button" className="btn btn-success btn-lg" onClick={handlePrintReceipt}>
                      Print Receipt
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </>
        )}
        {/* Replace the existing filter and receipt table containers with this new structure */}
        <button className="btn btn-secondary mb-3" onClick={toggleContainers}>
          {containersVisible ? "Hide" : "Show"} Filters and Receipts
        </button>

        {containersVisible && (
          <>
            <div className="card shadow-lg ">
              <div className="card-body">
                <h4 className="card-title mb-4">Filter Receipts</h4>
                <form onSubmit={handleFilterSubmit}>
                  <div className="row g-3">
                    <div className="col-md-3">
                      <div className="form-check form-switch mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="globalFilterToggle"
                          checked={globalFilter}
                          onChange={(e) => {
                            setGlobalFilter(e.target.checked)
                            if (e.target.checked) {
                              fetchAllReceipts()
                            } else {
                              fetchReceiptData(dateFilter.mobileNumber)
                            }
                          }}
                        />
                        <label className="form-check-label" htmlFor="globalFilterToggle">
                          Show All Receipts
                        </label>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Filter Type</label>
                      <select className="form-select" value={filterType} onChange={handleFilterTypeChange}>
                        <option value="all">Show All</option>
                        <option value="today">Today</option>
                        <option value="month">This Month</option>
                        <option value="custom">Custom Date Range</option>
                      </select>
                    </div>
                    {filterType === "custom" && (
                      <div className="row mt-3">
                        <div className="col-md-6">
                          <label className="form-label">From Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={customDateRange.fromDate}
                            onChange={(e) => setCustomDateRange((prev) => ({ ...prev, fromDate: e.target.value }))}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">To Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={customDateRange.toDate}
                            onChange={(e) => setCustomDateRange((prev) => ({ ...prev, toDate: e.target.value }))}
                          />
                        </div>
                      </div>
                    )}
                    <div className="col-md-3 d-flex align-items-end">
                      <button type="submit" className="btn btn-primary me-2">
                        Apply Filter
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={resetFilters}>
                        Reset
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
            {/* Receipt Table - Always visible */}
            <div className="card shadow-lg">
              <div className="card-body">
                <h4 className="card-title mb-4">{globalFilter ? "All Receipts" : "Receipt Details"}</h4>
                {(filterType === "month" || filterType === "custom") && dateFilter.startDate && dateFilter.endDate && (
                  <div className="mb-3">
                    <strong>Date Range: </strong>
                    {`${dateFilter.startDate} to ${dateFilter.endDate}`}
                  </div>
                )}
                <div className="table-responsive">
                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-warning btn-lg "
                      style={{ marginBottom: "10px" }}
                      onClick={generatePdf}
                    >
                      Report Pdf
                    </button>
                  </div>

                  <table className="table table-striped table-hover">
                    <thead className="table-dark">
                      <tr>
                        <th scope="col">Sno</th>
                        <th scope="col">ReceiptNo</th>
                        <th scope="col">Name</th>
                        <th scope="col">Amount</th>
                        <th scope="col">Date</th>
                        <th scope="col">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receipt.map((receipt, index) => (
                        <tr key={index}>
                          <th scope="row">{index + 1}</th>
                          <td>{receipt.ReceiptNo}</td>
                          <td>{receipt.Name}</td>
                          <td>{receipt.Amount}</td>
                          <td>{format(new Date(receipt.Date), "dd/MM/yyyy")}</td>
                          <td>
                            <button className="btn btn-primary btn-sm" onClick={() => generateReceipt(receipt)}>
                              Print
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="3" className="text-end">
                          <strong>Grand Total:</strong>
                        </td>
                        <td colSpan="3">
                          <strong>{grandTotal.toFixed(2)}</strong>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/*recipt template*/}

      {/* Filter Options */}
      {/*This section has been removed because it was duplicating functionality already present in the code.*/}

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  )
}

export default Receipt

